// src/pages/SubCategoriesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft } from 'lucide-react';
import apiClient from '../api/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import VerticalContentCard from '../components/Category/VerticalContentCard';
import ContentFilters from '../components/common/ContentFilters';

const SPECIAL_SLUGS_MAP = {
    'latest': 'جدیدترین محتوا',
    'free': 'محتوای رایگان',
    'categories': 'دسته‌بندی‌ها',
};

const getDefaultTitle = (slug) => {
    if (!slug) return 'لیست محصولات';
    if (SPECIAL_SLUGS_MAP[slug]) return SPECIAL_SLUGS_MAP[slug];
    return slug.replace(/-/g, ' ')
               .split(' ')
               .map(word => word.charAt(0).toUpperCase() + word.slice(1))
               .join(' ');
};

const FIXED_COLORS = ['#4f06ceff', '#5d8ffcff', '#049d91ff'];
const getFixedColor = (index) => FIXED_COLORS[index % FIXED_COLORS.length];

const SubCategoriesPage = () => {
    const { categorySlug, subcategorySlug } = useParams();

    const [products, setProducts] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [categoryInfo, setCategoryInfo] = useState({
        title: getDefaultTitle(subcategorySlug || categorySlug || 'لیست محصولات'),
        parentTitle: getDefaultTitle('categories'),
        parentSlug: categorySlug
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        sortOrder: 'newest',
        isFree: null,
    });

    const fetchContent = useCallback(async () => {
        if (!categorySlug) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        setProducts([]);
        setSubcategories([]);

        try {
            let productsUrl = `/products/list/${categorySlug}/`;
            if (subcategorySlug) {
                productsUrl = `/products/list/${categorySlug}/${subcategorySlug}/`;
            }
            const params = {
                sort_by: filters.sortOrder,
                ...(filters.isFree !== null && { is_free: filters.isFree })
            };
            const productsPromise = apiClient.get(productsUrl, { params });

            let categoriesPromise = Promise.resolve(null);
            if (!subcategorySlug) {
                categoriesPromise = apiClient.get('/products/categories/all/');
            }

            const [productsResponse, categoriesResponse] = await Promise.all([
                productsPromise,
                categoriesPromise
            ]);

            const productsData = productsResponse.data.results || productsResponse.data;
            setProducts(productsData);

            let pageTitle = getDefaultTitle(subcategorySlug || categorySlug);
            let pageParentTitle = getDefaultTitle('categories');
            let filteredChildren = [];

            if (subcategorySlug) {
                if (categoriesResponse && categoriesResponse.data) {
                    const allCategories = categoriesResponse.data;
                    const parentCat = allCategories.find(c => c.slug === categorySlug);
                    if (parentCat) {
                        pageParentTitle = parentCat.title || parentCat.name;   // اصلاح
                        const childCat = parentCat.children?.find(c => c.slug === subcategorySlug);
                        if (childCat) pageTitle = childCat.title || childCat.name;   // اصلاح
                    }
                }
            } else {
                if (categoriesResponse && categoriesResponse.data) {
                    const allCategories = categoriesResponse.data;
                    const currentCategory = allCategories.find(c => c.slug === categorySlug);
                    if (currentCategory) {
                        pageTitle = currentCategory.title || currentCategory.name;   // اصلاح
                        pageParentTitle = currentCategory.parent?.title || currentCategory.parent?.name || getDefaultTitle('categories');
                        filteredChildren = (currentCategory.children || []).filter(child => (child.product_count || 0) > 0);
                        setSubcategories(filteredChildren);
                    }
                }
            }

            setCategoryInfo({
                title: pageTitle,
                parentTitle: pageParentTitle,
                parentSlug: categorySlug
            });

        } catch (err) {
            console.error("❌ خطا در واکشی:", err);
            let message = "در بارگذاری محتوای دسته‌بندی خطایی رخ داد.";
            if (err.response?.status === 404) {
                message = `محتوای دسته‌بندی ${getDefaultTitle(subcategorySlug || categorySlug)} یافت نشد.`;
            }
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [categorySlug, subcategorySlug, filters]);

    useEffect(() => {
        fetchContent();
    }, [fetchContent]);

    if (loading) {
        return <LoadingSpinner text={`در حال بارگذاری ${categoryInfo.title}...`} />;
    }

    if (error) {
        return (
            <div className="text-center text-red-600 p-4">
                <p>{error}</p>
            </div>
        );
    }

    const defaultOgImage = 'https://s3.afra.kubit.cloud/mahsacanva1365-far-free/profile/og-image.jpg';
    const pageTitle = categoryInfo.title ? `${categoryInfo.title} | فرزانه` : 'دسته‌بندی محصولات | فرزانه';
    const pageDescription = categoryInfo.title
        ? `مشاهده تمام محصولات دسته‌بندی «${categoryInfo.title}» در فرزانه. شامل درس‌گفتارها، کتاب‌ها و دوره‌های آموزشی مرتبط.`
        : 'لیست محصولات بر اساس دسته‌بندی در وب‌اپلیکیشن فرزانه';

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:image" content={defaultOgImage} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content={defaultOgImage} />
            </Helmet>

            <div className="sub-categories-page rtl container mx-auto p-0 pb-20 max-w-[460px] overflow-x-hidden bg-white">
                <div className="p-4 bg-white border-b border-gray-200">
                    <nav className="text-[11px] font-light text-gray-500 mb-2 flex items-center space-x-reverse space-x-1">
                        <Link to="/categories" className="hover:text-indigo-600 transition">
                            {getDefaultTitle('categories')}
                        </Link>
                        {subcategorySlug && categorySlug && (
                            <React.Fragment>
                                <ChevronLeft size={14} className="mt-0.5 text-gray-400" />
                                <Link to={`/products/list/${categorySlug}`} className="hover:text-indigo-600 transition">
                                    {categoryInfo.parentTitle}
                                </Link>
                            </React.Fragment>
                        )}
                        <ChevronLeft size={14} className="mt-0.5 text-gray-400" />
                        <span className="text-gray-800 font-medium">
                            {categoryInfo.title}
                        </span>
                    </nav>
                    <h1 className="text-2xl font-extrabold text-gray-800 truncate">
                        {categoryInfo.title}
                    </h1>
                </div>

                <ContentFilters filters={filters} setFilters={setFilters} />

                <div className="p-4 md:p-6">
                    {subcategories.length > 0 && (
                        <div className="subcategories-section mb-8">
                            <h2 className="text-[11px] font-normal text-gray-800 mb-4 border-b pb-2">
                                زیردسته‌بندی‌ها
                            </h2>
                            <div className="grid grid-cols-2 gap-4">
                                {subcategories.map((child, index) => {
                                    const borderColor = getFixedColor(index);
                                    return (
                                        <Link
                                            key={child.slug}
                                            to={`/products/list/${categorySlug}/${child.slug}`}
                                            className="relative block w-full h-[60px] bg-white border-2 
                                                       rounded-xl overflow-hidden group 
                                                       flex items-center justify-center p-4 text-center rtl"
                                            style={{ borderColor: borderColor }}
                                        >
                                            <h3
                                                className="relative z-10 text-[13px] font-bold leading-tight line-clamp-2"
                                                style={{ color: borderColor }}
                                            >
                                                {child.title || child.name}
                                            </h3>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="products-section">
                        {products.length === 0 && subcategories.length === 0 ? (
                            <div className="text-center text-gray-500 py-10">
                                <p>هیچ محتوایی در این دسته‌بندی یافت نشد.</p>
                            </div>
                        ) : products.length > 0 ? (
                            <div className="grid grid-cols-1 gap-4">
                                {products.map(product => (
                                    <VerticalContentCard key={product.id || product.slug} data={product} />
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default SubCategoriesPage;